import React from 'react';

import { ENVS, getEnv, getApigeeUrl } from '@adsk/acs-sc-web-platform-api';
import {
  DocumentArrowLeftRightIcon,
  MoneyArrowInIcon,
  MoneyArrowOutIcon,
} from '@adsk/alloy-react-icon';
import StatusColorIndicator from '@adsk/alloy-react-status-color-indicator';
import theme from '@adsk/alloy-react-theme';

export const BADGE_STATUS_COLORS = {
  BLUE: theme.colors.adskBlue500,
  YELLOW: theme.colors.yellowOrange500,
  GREEN: theme.colors.green500,
  PURPLE: theme.colors.purple500,
  RED: theme.colors.red500,
  GRAY_LIGHT: theme.colors.charcoal200,
  GRAY: theme.colors.charcoal700,
  GRAY_DARK: theme.colors.charcoal900,
  WHITE: theme.colors.white,
  TURQUOISE: theme.colors.turquoise500,
  DARK_BLUE: theme.colors.darkBlue300,
};

export const COST_ENV_CONFIG_KEYS = {
  COST_BE: 'COST_BE',
  ACC_FE: 'ACC_FE',
};

const isChangeOrder = (type) =>
  ['pco', 'oco', 'sco', 'rfq', 'rco'].includes(type.toLowerCase());

const BE_CONFIG = {
  [ENVS.QA]: '/construction/cost-qa',
  [ENVS.STAGING]: '/construction/cost',
  [ENVS.PRODUCTION]: '/construction/cost',
};

export const getBaseUrl = () => {
  const env = getEnv();
  return `${getApigeeUrl()}${BE_CONFIG[env]}`;
};

export const STATUSES = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ACCEPTED: 'accepted',
  COSTACCEPTED: 'costAccepted',
  BUDGETACCEPTED: 'budgetAccepted',
  COSTPAYMENTACCEPTED: 'costPaymentAccepted',
  CLOSED: 'closed',
  EXECUTED: 'executed',
  OPEN: 'open',
  PAID: 'paid',
  PENDINGINPUT: 'pendingInput',
  PRICING: 'pricing',
  PROPOSED: 'proposed',
  REVIEW: 'review',
  REVISE: 'revise',
  REVISING: 'revising',
  SENT: 'sent',
  SIGNED: 'signed',
  SIGNEDBY: 'signedBy',
  SUBMITTED: 'submitted',
  VOID: 'void',
  INREVIEW: 'inReview',
};

export const PCO_STATUSES = {
  DRAFT: 'draft',
  OPEN: 'open',
  PRICING: 'pricing',
  PROPOSED: 'proposed',
  APPROVED: 'approved',
  EXECUTED: 'executed',
  REJECTED: 'rejected',
  REVISING: 'revising',
  ACCEPTED: 'accepted',
  COSTACCEPTED: 'costAccepted',
  BUDGETACCEPTED: 'budgetAccepted',
};

export const PCO_STATUS_MAPPING = {
  [PCO_STATUSES.DRAFT]: STATUSES.DRAFT,
  [PCO_STATUSES.OPEN]: STATUSES.PENDING,
  [PCO_STATUSES.PRICING]: STATUSES.PENDING,
  [PCO_STATUSES.PROPOSED]: STATUSES.PENDING,
  [PCO_STATUSES.APPROVED]: STATUSES.APPROVED,
  [PCO_STATUSES.EXECUTED]: STATUSES.APPROVED,
  [PCO_STATUSES.REJECTED]: STATUSES.REJECTED,
  [PCO_STATUSES.REVISING]: STATUSES.PENDING,
  [PCO_STATUSES.ACCEPTED]: STATUSES.PENDING,
  [PCO_STATUSES.COSTACCEPTED]: STATUSES.PENDING,
  [PCO_STATUSES.BUDGETACCEPTED]: STATUSES.PENDING,
};

export const CONTRACT_STATUS_MAPPING = {
  pending: STATUSES.PENDINGINPUT,
};

export const getStatus = (type, entity) => {
  const { costStatus, budgetStatus, status } = entity;
  const mappedCostStatus =
    costStatus === STATUSES.ACCEPTED ? 'costAccepted' : costStatus;

  const mappedBudgetStatus =
    budgetStatus === STATUSES.ACCEPTED ? 'budgetAccepted' : budgetStatus;

  let result;
  if (type === 'pco') {
    result = PCO_STATUS_MAPPING[mappedCostStatus];
  } else if (['rfq', 'sco'].includes(type)) {
    result = mappedCostStatus;
  } else if (['rco', 'oco'].includes(type)) {
    result = mappedBudgetStatus;
  } else if (['contract'].includes(type)) {
    result = CONTRACT_STATUS_MAPPING[status] || status;
  } else if (
    ['budgetPayApp', 'costPayApp', 'mainContract', 'expense'].includes(type)
  ) {
    result =
      type === 'costPayApp' && status === 'accepted'
        ? 'costPaymentAccepted'
        : status;
  }
  return result || STATUSES.VOID;
};

export const STATUS_COLOR_MAPPING = {
  [STATUSES.DRAFT]: StatusColorIndicator.COLORS.GRAY_DARK,
  [STATUSES.PENDING]: StatusColorIndicator.COLORS.YELLOW,
  [STATUSES.APPROVED]: StatusColorIndicator.COLORS.GREEN,
  [STATUSES.REJECTED]: StatusColorIndicator.COLORS.RED,

  [STATUSES.ACCEPTED]: StatusColorIndicator.COLORS.TURQUOISE,
  [STATUSES.COSTACCEPTED]: StatusColorIndicator.COLORS.TURQUOISE,
  [STATUSES.BUDGETACCEPTED]: StatusColorIndicator.COLORS.TURQUOISE,
  [STATUSES.COSTPAYMENTACCEPTED]: StatusColorIndicator.COLORS.TURQUOISE,
  [STATUSES.CLOSED]: StatusColorIndicator.COLORS.GRAY_LIGHT,
  [STATUSES.EXECUTED]: StatusColorIndicator.COLORS.GREEN,
  [STATUSES.OPEN]: StatusColorIndicator.COLORS.YELLOW,
  [STATUSES.PAID]: StatusColorIndicator.COLORS.GREEN,
  [STATUSES.PENDINGINPUT]: StatusColorIndicator.COLORS.PURPLE,
  [STATUSES.PRICING]: StatusColorIndicator.COLORS.PURPLE,
  [STATUSES.PROPOSED]: StatusColorIndicator.COLORS.BLUE,
  [STATUSES.REVIEW]: StatusColorIndicator.COLORS.YELLOW,
  [STATUSES.REVISE]: StatusColorIndicator.COLORS.PURPLE,
  [STATUSES.REVISING]: StatusColorIndicator.COLORS.PURPLE,
  [STATUSES.SENT]: StatusColorIndicator.COLORS.PURPLE,
  [STATUSES.SIGNED]: StatusColorIndicator.COLORS.GREEN,
  [STATUSES.SUBMITTED]: StatusColorIndicator.COLORS.PURPLE,
  [STATUSES.VOID]: StatusColorIndicator.COLORS.GRAY,
  [STATUSES.INREVIEW]: StatusColorIndicator.COLORS.YELLOW,
};

export const BADGE_STATUS_COLOR_MAPPING = {
  [STATUSES.DRAFT]: BADGE_STATUS_COLORS.GRAY_DARK,
  [STATUSES.PENDING]: BADGE_STATUS_COLORS.YELLOW,
  [STATUSES.APPROVED]: BADGE_STATUS_COLORS.GREEN,
  [STATUSES.REJECTED]: BADGE_STATUS_COLORS.RED,

  [STATUSES.ACCEPTED]: BADGE_STATUS_COLORS.TURQUOISE,
  [STATUSES.COSTACCEPTED]: BADGE_STATUS_COLORS.TURQUOISE,
  [STATUSES.BUDGETACCEPTED]: BADGE_STATUS_COLORS.TURQUOISE,
  [STATUSES.COSTPAYMENTACCEPTED]: BADGE_STATUS_COLORS.TURQUOISE,
  [STATUSES.CLOSED]: BADGE_STATUS_COLORS.GRAY_LIGHT,
  [STATUSES.EXECUTED]: BADGE_STATUS_COLORS.GREEN,
  [STATUSES.OPEN]: BADGE_STATUS_COLORS.YELLOW,
  [STATUSES.PAID]: BADGE_STATUS_COLORS.GREEN,
  [STATUSES.PENDINGINPUT]: BADGE_STATUS_COLORS.PURPLE,
  [STATUSES.PRICING]: BADGE_STATUS_COLORS.PURPLE,
  [STATUSES.PROPOSED]: BADGE_STATUS_COLORS.BLUE,
  [STATUSES.REVIEW]: BADGE_STATUS_COLORS.YELLOW,
  [STATUSES.REVISE]: BADGE_STATUS_COLORS.PURPLE,
  [STATUSES.REVISING]: BADGE_STATUS_COLORS.PURPLE,
  [STATUSES.SENT]: BADGE_STATUS_COLORS.PURPLE,
  [STATUSES.SIGNED]: BADGE_STATUS_COLORS.GREEN,
  [STATUSES.SUBMITTED]: BADGE_STATUS_COLORS.PURPLE,
  [STATUSES.VOID]: BADGE_STATUS_COLORS.GRAY,
  [STATUSES.INREVIEW]: BADGE_STATUS_COLORS.YELLOW,
};

export const COST_TYPE = [
  'pco',
  'oco',
  'sco',
  'rfq',
  'rco',
  'budget',
  'budgetPayApp',
  'contract',
  'costPayApp',
  'mainContract',
  'expense',
];
const NAME_MAPPING = {
  budget: 'budgets',
  contract: 'contracts',
  budgetPayApp: 'budget-payments',
  costPayApp: 'cost-payments/list',
  mainContract: 'main-contracts',
  expense: 'expenses',
};

export const withProps = (Element, type) =>
  Object.assign((props) => <Element {...props} type={type} />, {
    displayName: Element.displayName || 'Component',
  });

export const MAPPING_REQUEST_PATH = {
  pco: 'change-orders/pco',
  oco: 'change-orders/oco',
  sco: 'change-orders/sco',
  rfq: 'change-orders/rfq',
  rco: 'change-orders/rco',
  budget: 'budgets',
  contract: 'contracts',
  budgetPayApp: 'payments',
  costPayApp: 'payments',
  mainContract: 'main-contracts',
  expense: 'expenses',
};

export const MAPPING_ICON = {
  pco: DocumentArrowLeftRightIcon.renderIcon,
  oco: DocumentArrowLeftRightIcon.renderIcon,
  sco: DocumentArrowLeftRightIcon.renderIcon,
  rfq: DocumentArrowLeftRightIcon.renderIcon,
  rco: DocumentArrowLeftRightIcon.renderIcon,
  budget: MoneyArrowInIcon.renderIcon,
  budgetPayApp: MoneyArrowInIcon.renderIcon,
  contract: MoneyArrowOutIcon.renderIcon,
  costPayApp: MoneyArrowOutIcon.renderIcon,
  mainContract: MoneyArrowInIcon.renderIcon,
  expense: MoneyArrowOutIcon.renderIcon,
};

export const MAPPING_TOOL_ID = {
  pco: 'CHANGE_ORDER',
  oco: 'CHANGE_ORDER',
  sco: 'CHANGE_ORDER',
  rfq: 'CHANGE_ORDER',
  rco: 'CHANGE_ORDER',
  budget: 'BUDGET',
  budgetPayApp: 'BUDGET',
  mainContract: 'BUDGET',
  contract: 'COST',
  costPayApp: 'COST',
  expense: 'COST',
};

export const MAPPING_SORT = {
  pco: 'number+desc',
  oco: 'number+desc',
  sco: 'number+desc',
  rfq: 'number+desc',
  rco: 'number+desc',
  budget: 'code+desc',
  contract: 'code+desc',
  budgetPayApp: 'number+desc',
  costPayApp: 'number+desc',
  expense: 'number+desc',
  mainContract: 'code+desc',
};

export const MAPPING_KEY = {
  pco: 'number',
  oco: 'number',
  sco: 'number',
  rfq: 'number',
  rco: 'number',
  budget: 'code',
  contract: 'code',
  mainContract: 'code',
  budgetPayApp: 'number',
  costPayApp: 'number',
  expense: 'number',
};

// refer to https://git.autodesk.com/BIM360/Autodesk.Nucleus.Service.Relationships/blob/master/src/Configuration/domains-v1/autodesk-bim360-cost.json
export const MAPPING_META_TYPE = {
  pco: 'pco',
  oco: 'oco',
  sco: 'sco',
  rfq: 'rfq',
  rco: 'rco',
  budget: 'budget',
  contract: 'contract',
  mainContract: 'maincontract',
  budgetPayApp: 'budgetpayment',
  costPayApp: 'costpayment',
  expense: 'expense',
};

export const getLink = (type, projectId, entity = {}) => {
  if (isChangeOrder(type)) {
    return `changeorder/projects/${projectId}/changeorders/${type}`;
  } else if (['budget', 'mainContract'].includes(type)) {
    return `budget/projects/${projectId}/${NAME_MAPPING[type]}`;
  } else if (['contract', 'costPayApp', 'expense'].includes(type)) {
    return `cost/projects/${projectId}/${NAME_MAPPING[type]}`;
  } else if (['budgetPayApp'].includes(type)) {
    return `budget/projects/${projectId}/${NAME_MAPPING[type]}/${entity.associationId}/${entity.billingPeriodId}`;
  } else {
    return '';
  }
};

export const shouldShowStatus = (type) =>
  [
    'pco',
    'oco',
    'sco',
    'rfq',
    'rco',
    'contract',
    'budgetPayApp',
    'costPayApp',
    'mainContract',
    'expense',
  ].includes(type)
    ? true
    : false;

export const MAPPING_TERM_PATH = {
  pco: 'pco',
  oco: 'oco',
  sco: 'sco',
  rfq: 'rfq',
  rco: 'rco',
  budget: 'budget',
  contract: 'contract',
  budgetPayApp: 'budget_payment',
  costPayApp: 'cost_payment',
  mainContract: 'main_contract',
  expense: 'expense',
  costAccepted: 'cost_item_cost_accepted',
  budgetAccepted: 'cost_item_budget_accepted',
  costPaymentAccepted: 'cost_payment_accepted',
};

export const MAPPING_PERMISSION = {
  pco: 'PCO',
  oco: 'OCO',
  sco: 'SCO',
  rfq: 'RFQ',
  rco: 'RCO',
  budget: 'Budget',
  contract: 'Contract',
  budgetPayApp: 'BudgetPayment',
  costPayApp: 'CostPayment',
  mainContract: 'MainContract',
  expense: 'ActualCost',
};

export const getLocalStatePermissionsKey = (projectId) =>
  `_REFERENCE_PICKER_COST_PERMISSION_${projectId}`;

export const PERMISSION_LEVEL = {
  VIEW_ALL: 'viewAll',
  FULL_CONTROL: 'fullControl',
  COLLABORATE: 'collaborate',
  COLLABORATE_CREATE: 'collaborateCreate',
  NO_ACCESS: 'noAccess',
};

export const ERROR_CODE = {
  NO_BUDGET_ON_CONTRACT: 450913,
};
